import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { PRICING_WHATSAPP_URL } from '../config/contact';
import './SimplePage.css';

const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function SolSection({ id, color, surface, flip, label, heading, desc, points, children }) {
  return (
    <section id={id} className={`sol-sec ${surface ? 'sol-sec--surface' : 'sol-sec--base'} section`}>
      <div className="container">
        <div className={`sol-inner${flip ? ' sol-inner--flip' : ''}`} style={{ '--sol-color': color }}>
          <div className="sol-copy">
            <span className="sol-badge">{label}</span>
            <h2 className="display-lg sol-copy__heading" dangerouslySetInnerHTML={{ __html: heading }} />
            <p className="text-lg sol-copy__desc">{desc}</p>
            <ul className="sol-copy__points">
              {points.map(p => (
                <li key={p}>
                  <span className="sol-check"><CheckIcon /></span>
                  {p}
                </li>
              ))}
            </ul>
            <div className="sol-actions">
              <a href="https://dashboard.zepliq.com" className="btn btn-primary">Start Free</a>
              <Link to="/contact" className="btn btn-secondary">Talk to Sales</Link>
            </div>
          </div>
          <div className="sol-visual-wrap">{children}</div>
        </div>
      </div>
    </section>
  );
}

export default function SolutionsPage() {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, particles = [];
    const PARTICLE_COUNT = 55;
    const resize = () => { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * W; this.y = Math.random() * H;
        this.r = Math.random() * 1.8 + 0.4;
        this.dx = (Math.random() - 0.5) * 0.25; this.dy = (Math.random() - 0.5) * 0.25;
        this.o = Math.random() * 0.5 + 0.15;
        this.hue = Math.random() > 0.6 ? 245 : 270;
      }
      update() { this.x += this.dx; this.y += this.dy; if (this.x < 0 || this.x > W || this.y < 0 || this.y > H) this.reset(); }
      draw() { ctx.beginPath(); ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2); ctx.fillStyle = `hsla(${this.hue}, 80%, 55%, ${this.o})`; ctx.fill(); }
    }
    for (let i = 0; i < PARTICLE_COUNT; i++) particles.push(new Particle());
    const drawLines = () => {
      for (let i = 0; i < particles.length; i++) for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x, dy = particles[i].y - particles[j].y;
        const d = Math.sqrt(dx*dx + dy*dy);
        if (d < 100) { ctx.beginPath(); ctx.moveTo(particles[i].x, particles[i].y); ctx.lineTo(particles[j].x, particles[j].y); ctx.strokeStyle = `rgba(99,102,241,${0.06*(1-d/100)})`; ctx.lineWidth = 0.5; ctx.stroke(); }
      }
    };
    const animate = () => { ctx.clearRect(0,0,W,H); particles.forEach(p=>{p.update();p.draw();}); drawLines(); animRef.current = requestAnimationFrame(animate); };
    animate();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(animRef.current); };
  }, []);

  return (
    <div className="sol-page">
      <SEO
        title="Solutions — WhatsApp Automation for E-Commerce, Healthcare, Real Estate & More"
        description="Zepliq powers WhatsApp automation for e-commerce, healthcare, real estate, education, travel, and SaaS businesses. Automate customer support, order confirmations, appointments, and lead generation."
        keywords="WhatsApp for ecommerce, WhatsApp for healthcare, WhatsApp real estate automation, WhatsApp education chatbot, WhatsApp travel bot, WhatsApp SaaS automation, business WhatsApp solutions, industry WhatsApp chatbot"
        canonical="/solutions"
      />

      {/* ── HERO ── */}
      <section className="sol-hero">
        <canvas ref={canvasRef} className="sol-hero__canvas" aria-hidden="true" />
        <div className="sol-hero__blob sol-hero__blob--1" aria-hidden="true" />
        <div className="sol-hero__blob sol-hero__blob--2" aria-hidden="true" />
        <div className="container sol-hero__inner">
          <span className="badge badge-brand">Solutions</span>
          <h1 className="display-xl sol-hero__title">
            Zepliq works for<br />
            <span className="gradient-text">every industry.</span>
          </h1>
          <p className="text-lg sol-hero__sub">
            One platform, built to automate customer conversations across every business type.
            See how Zepliq fits your industry.
          </p>
          <div className="sol-hero__actions">
            <Link to="/contact" className="btn btn-primary btn-lg">
              Start Free
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
            <a href={PRICING_WHATSAPP_URL} className="btn btn-secondary btn-lg" target="_blank" rel="noopener noreferrer">Get Pricing on WhatsApp</a>
          </div>
        </div>
      </section>

      {/* ── E-COMMERCE ── */}
      <SolSection
        id="ecommerce"
        color="#96BF48"
        surface={false}
        flip={false}
        label="E-Commerce Stores"
        heading="Confirm every order.<br />Recover every cart."
        desc="Connect Zepliq to your Shopify or WooCommerce store and automate the entire post-purchase conversation, from order confirmation to follow-up to cancellation handling."
        points={[
          'Instant WhatsApp order confirmations on every purchase',
          'Automated follow-ups for unconfirmed COD orders',
          'AI reads YES/NO replies and updates order status automatically',
          'Abandoned cart recovery via WhatsApp',
          'Product catalog and FAQ automation',
        ]}
      >
        <div className="sol-mockup">
          <div className="sol-mockup__bar">
            <span className="sol-mockup__dot" />
            <span className="sol-mockup__bar-title">WhatsApp · Order Bot</span>
            <span className="sol-mockup__status">Live</span>
          </div>
          <div className="sol-mockup__chat">
            <div className="sol-bubble sol-bubble--out">
              Hi Ahmed! Your order <strong>#WC-4821</strong> is placed.<br />
              2&times; Navy Hoodie (M) &middot; $59.98<br /><br />
              Reply <strong>YES</strong> to confirm or <strong>NO</strong> to cancel.
            </div>
            <div className="sol-bubble sol-bubble--in">YES</div>
            <div className="sol-bubble sol-bubble--out">
              Order confirmed! Expected delivery: 3&ndash;5 business days.
            </div>
          </div>
          <div className="sol-mockup__foot">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            AI replied in <strong>0.3s</strong> &middot; Order auto-confirmed
          </div>
        </div>
      </SolSection>

      {/* ── HEALTHCARE ── */}
      <SolSection
        id="healthcare"
        color="#0EA5E9"
        surface
        flip
        label="Healthcare & Clinics"
        heading="Appointment bookings<br />and patient queries on autopilot."
        desc="Allow patients to book appointments, get test result reminders, and ask common questions, all through WhatsApp, 24/7, without burdening your reception staff."
        points={[
          'Appointment booking and confirmation automation',
          'Medication and follow-up reminders',
          'FAQ automation for common patient questions',
          'Lab result notification messages',
          'Multi-language support for diverse patient populations',
        ]}
      >
        <div className="sol-mockup">
          <div className="sol-mockup__bar">
            <span className="sol-mockup__dot" />
            <span className="sol-mockup__bar-title">Clinic Bot &middot; Appointments</span>
            <span className="sol-mockup__status">24/7</span>
          </div>
          <div className="sol-mockup__card">
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Patient</span>
              <span className="sol-mockup__val">Maria Hassan</span>
            </div>
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Doctor</span>
              <span className="sol-mockup__val">Dr. Sarah Ahmed</span>
            </div>
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Department</span>
              <span className="sol-mockup__val">Dermatology</span>
            </div>
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Date &amp; Time</span>
              <span className="sol-mockup__val--accent">Tomorrow &middot; 10:30 AM</span>
            </div>
            <button className="sol-mockup__btn">Confirm Appointment</button>
          </div>
          <div className="sol-mockup__foot">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
            Reminder auto-sent 24h before visit
          </div>
        </div>
      </SolSection>

      {/* ── REAL ESTATE ── */}
      <SolSection
        id="realestate"
        color="#D97706"
        surface={false}
        flip={false}
        label="Real Estate"
        heading="Qualify leads<br />while you sleep."
        desc="Automatically respond to property inquiries, qualify buyer and renter intent, and schedule viewings, all through WhatsApp without lifting a finger."
        points={[
          'Instant response to property inquiry messages',
          'AI qualification: budget, location, and timeline detection',
          'Automated viewing scheduling via WhatsApp',
          'Property catalog with images and pricing',
          'Lead pipeline with AI stage advancement',
        ]}
      >
        <div className="sol-mockup">
          <div className="sol-mockup__bar">
            <span className="sol-mockup__dot" />
            <span className="sol-mockup__bar-title">RealtyBot &middot; Lead Qualifier</span>
            <span className="sol-mockup__status">Active</span>
          </div>
          <div className="sol-mockup__chat">
            <div className="sol-bubble sol-bubble--in">Interested in the 3BR in DHA Phase 5</div>
            <div className="sol-bubble sol-bubble--out">
              Great choice! What is your budget range and move-in timeline?
            </div>
            <div className="sol-bubble sol-bubble--in">Around 1.8Cr, moving in 2 months</div>
            <div className="sol-bubble sol-bubble--out">
              Perfect match! I can arrange a viewing this Saturday. Shall I confirm?
            </div>
          </div>
          <div className="sol-mockup__foot">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/></svg>
            Lead qualified &middot; Stage: <strong>Viewing Scheduled</strong>
          </div>
        </div>
      </SolSection>

      {/* ── EDUCATION ── */}
      <SolSection
        id="education"
        color="#8B5CF6"
        surface
        flip
        label="Education & Coaching"
        heading="Enroll more students.<br />Support them better."
        desc="Answer course inquiries, send enrollment reminders, and support students through WhatsApp, automatically, around the clock."
        points={[
          'Course inquiry response and enrollment automation',
          'Class schedule and deadline reminders',
          'Assignment submission confirmations',
          'Student FAQ automation',
          'Broadcast messages for announcements and updates',
        ]}
      >
        <div className="sol-mockup">
          <div className="sol-mockup__bar">
            <span className="sol-mockup__dot" />
            <span className="sol-mockup__bar-title">EduBot &middot; Course Enrollment</span>
            <span className="sol-mockup__status">Open</span>
          </div>
          <div className="sol-mockup__card">
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Course</span>
              <span className="sol-mockup__val">Python for Beginners</span>
            </div>
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Next Batch</span>
              <span className="sol-mockup__val--accent">May 5, 2025</span>
            </div>
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Duration</span>
              <span className="sol-mockup__val">8 Weeks &middot; Online</span>
            </div>
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Seats Left</span>
              <span className="sol-mockup__val" style={{ color: '#EF4444', fontWeight: 700 }}>3 remaining</span>
            </div>
            <button className="sol-mockup__btn">Enroll Now &rarr;</button>
          </div>
          <div className="sol-mockup__foot">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="22 10 12 5 2 10 12 15 22 10"/></svg>
            12 students enrolled this week
          </div>
        </div>
      </SolSection>

      {/* ── TRAVEL ── */}
      <SolSection
        id="travel"
        color="#06B6D4"
        surface={false}
        flip={false}
        label="Travel & Hospitality"
        heading="Booking confirmations<br />and travel support, automated."
        desc="Handle booking confirmations, itinerary sharing, check-in reminders, and travel support queries, all through WhatsApp without a human agent."
        points={[
          'Booking confirmation and itinerary messages',
          'Check-in reminders and travel updates',
          'Visa and travel document FAQ automation',
          'Upsell tours, transfers, and upgrades via AI',
          'Multi-language support for international travelers',
        ]}
      >
        <div className="sol-mockup">
          <div className="sol-mockup__bar">
            <span className="sol-mockup__dot" />
            <span className="sol-mockup__bar-title">TravelBot &middot; Trip Confirmed</span>
            <span className="sol-mockup__status">Confirmed</span>
          </div>
          <div className="sol-mockup__card">
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Route</span>
              <span className="sol-mockup__val--accent">DXB &rarr; LHR</span>
            </div>
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Date</span>
              <span className="sol-mockup__val">May 10, 2025 &middot; 2 Pax</span>
            </div>
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Booking Ref</span>
              <span className="sol-mockup__val">#TRV-8823</span>
            </div>
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Check-in Opens</span>
              <span className="sol-mockup__val">May 10 &middot; 6:00 AM</span>
            </div>
            <div className="sol-mockup__card-row">
              <span className="sol-mockup__label">Status</span>
              <span className="sol-mockup__val" style={{ color: '#22C55E', fontWeight: 700 }}>Confirmed</span>
            </div>
          </div>
          <div className="sol-mockup__foot">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>
            Itinerary sent to WhatsApp
          </div>
        </div>
      </SolSection>

      {/* ── AGENCIES ── */}
      <SolSection
        id="agencies"
        color="#EC4899"
        surface
        flip
        label="Marketing Agencies"
        heading="Deliver AI automation<br />under your brand."
        desc="Resell Zepliq to your clients as a white-label product. Manage all client accounts from one dashboard and earn recurring revenue."
        points={[
          'White-label branding for client-facing dashboards',
          'Manage unlimited client accounts from one view',
          'Recurring revenue through agency partner program',
          'Co-marketing and lead generation support',
          'Dedicated partner account manager',
        ]}
      >
        <div className="sol-mockup">
          <div className="sol-mockup__bar">
            <span className="sol-mockup__dot" />
            <span className="sol-mockup__bar-title">AgencyDash &middot; 3 Clients</span>
            <span className="sol-mockup__status">Live</span>
          </div>
          <div className="sol-mockup__clients">
            <div className="sol-mockup__client">
              <span className="sol-mockup__client-dot" />
              <div className="sol-mockup__client-info">
                <span className="sol-mockup__client-name">TechBrand.co</span>
                <span className="sol-mockup__client-meta">WhatsApp &middot; 420 msgs/day</span>
              </div>
              <span className="sol-mockup__client-badge">Active</span>
            </div>
            <div className="sol-mockup__client">
              <span className="sol-mockup__client-dot" />
              <div className="sol-mockup__client-info">
                <span className="sol-mockup__client-name">ShopFast.pk</span>
                <span className="sol-mockup__client-meta">Shopify &middot; 85 orders/day</span>
              </div>
              <span className="sol-mockup__client-badge">Active</span>
            </div>
            <div className="sol-mockup__client">
              <span className="sol-mockup__client-dot" />
              <div className="sol-mockup__client-info">
                <span className="sol-mockup__client-name">LeanCRM.io</span>
                <span className="sol-mockup__client-meta">Email &middot; 210 leads/day</span>
              </div>
              <span className="sol-mockup__client-badge">Active</span>
            </div>
          </div>
          <div className="sol-mockup__foot">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/></svg>
            All managed from <strong>one dashboard</strong>
          </div>
        </div>
      </SolSection>

      {/* ── SAAS ── */}
      <SolSection
        id="saas"
        color="#6366F1"
        surface={false}
        flip={false}
        label="SaaS & Tech"
        heading="Support, onboard, and retain<br />users through WhatsApp."
        desc="Use Zepliq to automate user onboarding messages, support tickets, feature announcements, and renewal reminders, all through WhatsApp."
        points={[
          'User onboarding message sequences',
          'Support ticket intake and triage via WhatsApp',
          'Feature announcement broadcasts',
          'Renewal and upsell reminders',
          'API and webhook integration for product data',
        ]}
      >
        <div className="sol-mockup">
          <div className="sol-mockup__bar">
            <span className="sol-mockup__dot" />
            <span className="sol-mockup__bar-title">OnboardBot &middot; alex@company.io</span>
            <span className="sol-mockup__status">Day 2</span>
          </div>
          <div className="sol-mockup__steps">
            <div className="sol-mockup__step">
              <span className="sol-mockup__step-num sol-mockup__step-num--done">&#10003;</span>
              <span className="sol-mockup__step-text sol-mockup__step-text--done">Account Created</span>
              <span className="sol-mockup__step-tag sol-mockup__step-tag--done">Done</span>
            </div>
            <div className="sol-mockup__step">
              <span className="sol-mockup__step-num sol-mockup__step-num--done">&#10003;</span>
              <span className="sol-mockup__step-text sol-mockup__step-text--done">WhatsApp Connected</span>
              <span className="sol-mockup__step-tag sol-mockup__step-tag--done">Done</span>
            </div>
            <div className="sol-mockup__step">
              <span className="sol-mockup__step-num sol-mockup__step-num--done">&#10003;</span>
              <span className="sol-mockup__step-text sol-mockup__step-text--done">AI Trained on Docs</span>
              <span className="sol-mockup__step-tag sol-mockup__step-tag--done">Done</span>
            </div>
            <div className="sol-mockup__step">
              <span className="sol-mockup__step-num">4</span>
              <span className="sol-mockup__step-text">Launch First Campaign</span>
              <span className="sol-mockup__step-tag sol-mockup__step-tag--active">In Progress</span>
            </div>
            <div className="sol-mockup__step">
              <span className="sol-mockup__step-num">5</span>
              <span className="sol-mockup__step-text">CRM Integration</span>
              <span className="sol-mockup__step-tag" style={{ background: 'var(--border-subtle)', color: 'var(--text-muted)' }}>Upcoming</span>
            </div>
          </div>
          <div className="sol-mockup__foot">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><polyline points="8 21 12 17 16 21"/></svg>
            Step 4 in progress &middot; <strong>2 steps to go</strong>
          </div>
        </div>
      </SolSection>


    </div>
  );
}
